// src/hooks/useCachedCollection.js
//
// Faz 7 — Firebase okuma maliyetini düşüren oturum-içi koleksiyon cache'i.
// Faz 9 (düzeltme) — cache artık REAKTİF: bir koleksiyona yazıp refresh/invalidate
// çağrıldığında, o koleksiyonu kullanan TÜM açık ekranlar (yalnızca yazan ekran
// değil) anında güncellenir. Eski davranışta her hook örneği kendi state'ini
// tutuyordu; invalidateCollection yalnızca modül Map'ini siliyor, açık ekranlara
// haber vermiyordu — bu yüzden "her işlemden sonra sayfayı yenilemem gerekiyor"
// ve "kaydettiğim halde listede görünmüyor" şikayetleri oluşuyordu.
//
// Sözleşme (geriye uyumlu):
//   useCachedCollection(name) -> { data, loading, error, refresh }
//   invalidateCollection(name)         // tek koleksiyon
//   invalidateCollections([...names])  // çok koleksiyon (bir yazma birkaçını etkiler)
//   clearAllCollectionCache()          // çıkışta
//
// Davranış:
//   - Cache TAZE iken (TTL içinde) hiç okuma yapılmaz.
//   - Cache BAYAT iken son veri anında gösterilir, arka planda sessizce tazelenir.
//   - refresh() / invalidateCollection() sunucudan taze okur ve o koleksiyonu
//     dinleyen HER hook örneğine yeni veriyi push eder.
//   - Yazma sonrası eski bir arka-plan okuması (yazmadan ÖNCE başlamış) taze veriyi
//     EZMEZ: her okuma bir sıra numarası taşır, yalnızca en son başlayan commit eder.

import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// Cache tazelik süresi. Bu süre içinde tekrar okuma yapılmaz; süre geçince veri
// "bayat" sayılır ve bir sonraki kullanımda arka planda tazelenir.
const TTL_MS = 2 * 60 * 1000; // 2 dakika

const cache = new Map();        // name -> { data: Array, ts: number }
const subscribers = new Map();  // name -> Set<listener(partialState)>
const bgInflight = new Map();   // name -> Promise (arka plan yükleme, dedup)
const committedSeq = new Map(); // name -> commit edilen en yüksek sıra no
let seqCounter = 0;

async function fetchCollection(name) {
  const snap = await getDocs(collection(db, name));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

function isFresh(name) {
  const entry = cache.get(name);
  return !!entry && Date.now() - entry.ts < TTL_MS;
}

function getSubs(name) {
  let s = subscribers.get(name);
  if (!s) { s = new Set(); subscribers.set(name, s); }
  return s;
}

function notifyData(name) {
  const entry = cache.get(name);
  const payload = { data: entry ? entry.data : [], loading: false, error: null };
  getSubs(name).forEach((l) => l(payload));
}

function notifyError(name, error) {
  getSubs(name).forEach((l) => l({ error, loading: false }));
}

// Tek okuma; yalnızca daha yeni bir okuma commit etmediyse cache'e yazar ve yayar.
// (Yazma sonrası başlayan taze okumanın, yazmadan önce başlamış eski okumayla
// ezilmesini önler.)
async function doFetch(name, seq) {
  const docs = await fetchCollection(name);
  const prev = committedSeq.get(name) || 0;
  if (seq >= prev) {
    committedSeq.set(name, seq);
    cache.set(name, { data: docs, ts: Date.now() });
    notifyData(name);
  }
  return docs;
}

// Mount/gezinme: taze ise okuma yok; değilse dedup'lı arka plan yükleme.
function ensureLoaded(name) {
  if (isFresh(name)) { notifyData(name); return Promise.resolve(); }
  let p = bgInflight.get(name);
  if (!p) {
    const seq = ++seqCounter;
    p = doFetch(name, seq)
      .catch((e) => { console.error(`'${name}' koleksiyonu yüklenirken hata:`, e); notifyError(name, e); })
      .finally(() => { bgInflight.delete(name); });
    bgInflight.set(name, p);
  }
  return p;
}

// Yazma sonrası: her zaman taze okur (eski inflight'ı KULLANMAZ), en son başlayan
// commit eder ve tüm dinleyicilere yayar.
function forceReload(name) {
  const seq = ++seqCounter;
  return doFetch(name, seq).catch((e) => {
    console.error(`'${name}' koleksiyonu tazelenirken hata:`, e);
    notifyError(name, e);
  });
}

// Bir koleksiyonun cache'ini geçersiz kılar. Açık ekran varsa hemen taze okuyup
// hepsine yayar; hiç açık ekran yoksa tembel bırakır (sonraki mount okur).
export function invalidateCollection(name) {
  cache.delete(name);
  committedSeq.delete(name);
  if (getSubs(name).size > 0) return forceReload(name);
  return Promise.resolve();
}

// Bir yazma birden çok koleksiyonu etkilediğinde topluca geçersiz kıl.
export function invalidateCollections(names) {
  return Promise.all((names || []).map((n) => invalidateCollection(n)));
}

// Tüm cache'i temizler (örn. çıkış yaparken). Aboneleri (mount olmuş bileşenlerin
// dinleyicileri) BİLEREK temizlemez — abonelikler bileşen yaşam döngüsüne bağlıdır;
// silinirse hâlâ ekranda olan bir bileşen sonraki tazelemede güncellenmezdi.
export function clearAllCollectionCache() {
  cache.clear();
  bgInflight.clear();
  committedSeq.clear();
}

/**
 * Bir Firestore koleksiyonunu oturum-içi REAKTİF cache ile okur.
 * @param {string} name Koleksiyon adı (örn. "customers").
 * @returns {{ data: Array, loading: boolean, error: any, refresh: () => Promise<void> }}
 */
export default function useCachedCollection(name) {
  const [state, setState] = useState(() => ({
    data: cache.get(name)?.data || [],
    loading: !cache.has(name),
    error: null,
  }));
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const listener = (partial) => { if (mounted.current) setState((s) => ({ ...s, ...partial })); };
    const subs = getSubs(name);
    subs.add(listener);

    // Mount anındaki durum: cache varsa göster, yoksa spinner.
    if (cache.has(name)) setState((s) => ({ ...s, data: cache.get(name).data, loading: false }));
    else setState((s) => ({ ...s, loading: true }));

    ensureLoaded(name);

    return () => {
      mounted.current = false;
      subs.delete(listener);
    };
  }, [name]);

  const refresh = useCallback(() => forceReload(name), [name]);

  return { data: state.data, loading: state.loading, error: state.error, refresh };
}
